import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ShieldCheck, Clock, CreditCard, Plus } from 'lucide-react';
interface PricingProps {
  selectedPlan: 'pro' | 'elite' | 'completo';
  onSelectPlan: (plan: 'pro' | 'elite' | 'completo') => void;
  id?: string;
}
const CHECKOUT_URLS: Record<'pro' | 'elite' | 'completo', string> = {
  pro: 'https://checkout.mundodigitalarg.com/cart/48278374154481:1',
  elite: 'https://checkout.mundodigitalarg.com/cart/48278419570929:1',
  completo: '#' // Placeholder for the new 47990 tier
};
export function Pricing({
  selectedPlan,
  onSelectPlan,
  id = 'pricing-section'
}: PricingProps) {
  return <section id={id} className="bg-white py-6 sm:py-24 scroll-mt-10" data-id="element-252">
      <div className="mx-auto max-w-3xl px-3 sm:px-6 lg:px-8" data-id="element-253">
        <div className="text-center mb-4 sm:mb-10" data-id="element-254">
          <h2 className="text-lg font-bold tracking-tight text-slate-900 sm:text-4xl mb-1 sm:mb-3" data-id="element-255">
            Elegí tu Megapack
          </h2>
          <p className="text-[10px] sm:text-lg text-slate-600" data-id="element-256">
            Todas las opciones incluyen los 6 bonos de guardia
          </p>
        </div>

        {/* Planes apilados verticalmente (estirados) */}
        <div className="flex flex-col gap-3 sm:gap-5 pt-3 sm:pt-4 mb-4 sm:mb-7" data-id="element-257">
          {/* PRO Plan */}
          <motion.button type="button" whileTap={{
          scale: 0.98
        }} onClick={() => onSelectPlan('pro')} aria-pressed={selectedPlan === 'pro'} className={`relative flex flex-col sm:flex-row sm:items-center justify-between text-left rounded-xl sm:rounded-2xl p-4 sm:p-6 cursor-pointer transition-all duration-200 border-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${selectedPlan === 'pro' ? 'border-blue-500 bg-blue-50/60 shadow-md' : 'border-slate-200 bg-white hover:border-blue-300'}`} data-id="element-258">
            <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6" data-id="element-259">
              <div className="absolute top-4 right-4 sm:static sm:flex-shrink-0" data-id="element-260">
                <div className={`w-4 h-4 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center ${selectedPlan === 'pro' ? 'border-blue-500' : 'border-slate-300'}`} data-id="element-261">
                  {selectedPlan === 'pro' && <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-blue-500" data-id="element-262" />}
                </div>
              </div>

              <div data-id="element-263">
                <h3 className="text-base sm:text-2xl font-bold text-slate-900 leading-none" data-id="element-264">
                  Guardia PRO
                </h3>
                <p className="text-[10px] sm:text-sm text-slate-500 mt-1 leading-tight" data-id="element-265">
                  6 Tomos Guardia + 6 Bonos
                </p>
              </div>

              <ul className="mt-2 sm:mt-0 space-y-1 sm:space-y-0 sm:flex sm:gap-4 text-[10px] sm:text-sm text-slate-600" data-id="element-266">
                <li className="flex gap-x-1.5 items-center" data-id="element-267">
                  <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-none text-blue-600" data-id="element-268" />
                  Acceso inmediato
                </li>
              </ul>
            </div>

            <div className="mt-3 sm:mt-0 flex flex-row sm:flex-col items-baseline sm:items-end gap-2 sm:gap-0" data-id="element-269">
              <span className="text-xs sm:text-base text-slate-400 line-through leading-none" data-id="element-270">
                $59.990
              </span>
              <span className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-none sm:mt-1" data-id="element-271">
                $24.990
              </span>
            </div>
          </motion.button>

          {/* Elite Plan */}
          <motion.button type="button" whileTap={{
          scale: 0.98
        }} onClick={() => onSelectPlan('elite')} aria-pressed={selectedPlan === 'elite'} className={`relative flex flex-col sm:flex-row sm:items-center justify-between text-left rounded-xl sm:rounded-2xl p-4 sm:p-6 cursor-pointer transition-all duration-200 border-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${selectedPlan === 'elite' ? 'border-blue-500 bg-blue-50/60 shadow-md' : 'border-slate-200 bg-white hover:border-blue-300'}`} data-id="element-272">
            <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 mt-2 sm:mt-0" data-id="element-273">
              <div className="absolute top-5 right-4 sm:static sm:flex-shrink-0" data-id="element-274">
                <div className={`w-4 h-4 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center ${selectedPlan === 'elite' ? 'border-blue-500' : 'border-slate-300'}`} data-id="element-275">
                  {selectedPlan === 'elite' && <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-blue-500" data-id="element-276" />}
                </div>
              </div>

              <div data-id="element-277">
                <h3 className="text-base sm:text-2xl font-bold text-slate-900 leading-none" data-id="element-278">
                  Guardia Elite
                </h3>
                <p className="text-[10px] sm:text-sm text-blue-700 font-medium mt-1 leading-tight" data-id="element-279">
                  Todo PRO + Blindaje Legal
                </p>
              </div>

              <ul className="mt-2 sm:mt-0 space-y-1 sm:space-y-0 sm:flex sm:gap-4 text-[10px] sm:text-sm text-slate-600" data-id="element-280">
                <li className="flex gap-x-1.5 items-center font-medium text-blue-800" data-id="element-281">
                  <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-none text-blue-600" data-id="element-282" />
                  Blindaje Legal Total
                </li>
              </ul>
            </div>

            <div className="mt-3 sm:mt-0 flex flex-row sm:flex-col items-baseline sm:items-end gap-2 sm:gap-0" data-id="element-283">
              <span className="text-xs sm:text-base text-slate-400 line-through leading-none" data-id="element-284">
                $79.990
              </span>
              <span className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-none sm:mt-1" data-id="element-285">
                $35.990
              </span>
            </div>
          </motion.button>

          {/* Sistema Completo Plan — preselected, "más elegida", highlighted */}
          <motion.button type="button" whileTap={{
          scale: 0.98
        }} onClick={() => onSelectPlan('completo')} aria-pressed={selectedPlan === 'completo'} className={`relative flex flex-col sm:flex-row sm:items-center justify-between text-left rounded-xl sm:rounded-2xl p-4 sm:p-6 cursor-pointer transition-all duration-200 border-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${selectedPlan === 'completo' ? 'border-teal-500 bg-teal-50/70 shadow-xl ring-2 ring-teal-500/30 sm:scale-[1.02]' : 'border-teal-200 bg-white hover:border-teal-300'}`} data-id="element-286">
            <div className="absolute -top-3 inset-x-0 flex justify-center" data-id="element-287">
              <span className="bg-teal-500 text-white text-[9px] sm:text-xs font-bold px-3 py-0.5 sm:px-4 sm:py-1 rounded-full uppercase tracking-wider shadow-sm whitespace-nowrap" data-id="element-288">
                ⭐ MÁS ELEGIDA
              </span>
            </div>

            <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 mt-2 sm:mt-0" data-id="element-289">
              <div className="absolute top-5 right-4 sm:static sm:flex-shrink-0" data-id="element-290">
                <div className={`w-4 h-4 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center ${selectedPlan === 'completo' ? 'border-teal-500' : 'border-slate-300'}`} data-id="element-291">
                  {selectedPlan === 'completo' && <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-teal-500" data-id="element-292" />}
                </div>
              </div>

              <div data-id="element-293">
                <h3 className="text-base sm:text-2xl font-bold text-slate-900 leading-none" data-id="element-294">
                  Pediatría Blindada
                </h3>
                <p className="text-[10px] sm:text-sm text-teal-700 font-medium mt-1 leading-tight" data-id="element-295">
                  Guardia + Legal + Salud Mental
                </p>
              </div>

              <ul className="mt-2 sm:mt-0 space-y-1 sm:space-y-0 sm:flex sm:gap-4 text-[10px] sm:text-sm text-slate-600" data-id="element-296">
                <li className="flex gap-x-1.5 items-center font-medium text-teal-800" data-id="element-297">
                  <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-none text-teal-600" data-id="element-298" />
                  Megapack Salud Mental (6 Tomos + 6 Bonos)
                </li>
              </ul>
            </div>

            <div className="mt-3 sm:mt-0 flex flex-row sm:flex-col items-baseline sm:items-end gap-2 sm:gap-0" data-id="element-299">
              <span className="text-xs sm:text-base text-slate-400 line-through leading-none" data-id="element-300">
                $99.990
              </span>
              <span className="text-2xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-none sm:mt-1" data-id="element-301">
                $47.990
              </span>
            </div>
          </motion.button>
        </div>

        {/* Shared Payment Block + buy button */}
        <motion.div key={selectedPlan} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-slate-900 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center shadow-xl" data-id="element-302">
          <p className="text-xs sm:text-base text-slate-300 mb-3 sm:mb-4" data-id="element-303">
            Elegiste:{' '}
            <strong className="text-white font-semibold" data-id="element-304">
              {selectedPlan === 'completo' ? 'Sistema Completo Pediatría Blindada' : selectedPlan === 'elite' ? 'Megapack Guardia Pediátrica Elite' : 'Megapack Guardia Pediátrica PRO'}
            </strong>
          </p>

          <motion.a whileTap={{
          scale: 0.97
        }} href={CHECKOUT_URLS[selectedPlan]} className="inline-flex w-full items-center justify-center rounded-lg sm:rounded-xl bg-blue-600 px-4 py-3 sm:px-8 sm:py-4 text-sm sm:text-lg font-bold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 transition-all mb-3 sm:mb-5" data-id="element-305">
            💥 QUIERO MI MEGAPACK AHORA
          </motion.a>

          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 sm:gap-4 text-[9px] sm:text-sm text-slate-400" data-id="element-306">
            <span className="flex items-center gap-1" data-id="element-307">
              <ShieldCheck className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-308" /> Pago seguro
            </span>
            <span className="hidden sm:inline" data-id="element-309">·</span>
            <span className="flex items-center gap-1" data-id="element-310">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-311" /> Acceso en 5 min
            </span>
            <span className="hidden sm:inline" data-id="element-312">·</span>
            <span className="flex items-center gap-1" data-id="element-313">
              <ShieldCheck className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-314" /> Garantía 7 días
            </span>
            <span className="hidden sm:inline" data-id="element-315">·</span>
            <span className="flex items-center gap-1" data-id="element-316">
              <CreditCard className="w-3 h-3 sm:w-4 sm:h-4" data-id="element-317" /> Mercado Pago
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}